import React from 'react';
import { Badge } from 'primereact/badge';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

interface CompleteOption {
  storeCode: string;
  storeName: string;
  totalAvailable: number;
  totalNeeded: number;
  itemInventory: Array<{
    productSku: string;
    productName: string;
    quantityAvailable: number;
    quantityNeeded: number;
  }>;
}

interface CompleteFulfillmentOptionsProps {
  completeOptions: CompleteOption[];
  selectedStoreCode: string | null;
  selectedLineItems: any[];
  onStoreSelect: (storeCode: string) => void;
}

interface TableRow {
  storeCode: string;
  storeName: string;
  productName: string;
  productSku: string;
  quantityNeeded: number;
  quantityAvailable: number;
}

export const CompleteFulfillmentOptions: React.FC<CompleteFulfillmentOptionsProps> = ({
  completeOptions,
  selectedStoreCode,
  selectedLineItems,
  onStoreSelect
}) => {
  if (completeOptions.length === 0) {
    return (
      <div className="text-center p-6 border-1 surface-border border-round bg-orange-50">
        <div className="mb-4">
          <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: 'var(--orange-500)' }}></i>
        </div>
        <h5 className="text-orange-900 mb-3 font-semibold">No Complete Fulfillment Available</h5>
        <p className="text-orange-700 mb-0 line-height-3">
          No single store can fulfill the complete order. Please select a partial fulfillment option or assign items individually.
        </p>
      </div>
    );
  }

  const prepareTableData = (store: CompleteOption): TableRow[] => {
    return store.itemInventory.map((item) => ({
      storeCode: store.storeCode,
      storeName: store.storeName,
      productName: item.productName,
      productSku: item.productSku,
      quantityNeeded: item.quantityNeeded,
      quantityAvailable: item.quantityAvailable
    }));
  };

  const storeNameTemplate = (rowData: TableRow) => {
    return (
      <div className="font-semibold text-primary">
        {rowData.storeName}
      </div>
    );
  };

  const productNameTemplate = (rowData: TableRow) => {
    return (
      <div>
        <div className="font-medium">{rowData.productName}</div>
        <div className="text-xs text-color-secondary mt-1">SKU: {rowData.productSku}</div>
      </div>
    );
  };

  const neededTemplate = (rowData: TableRow) => {
    return (
      <div className="text-center">
        <span className="font-semibold">{rowData.quantityNeeded}</span>
      </div>
    );
  };

  const availableTemplate = (rowData: TableRow) => {
    return (
      <div className="text-center">
        <span className="text-color-secondary">{rowData.quantityAvailable}</span>
      </div>
    );
  };

  const handleAccordionChange = (storeCode: string) => {
    onStoreSelect(storeCode);
  };

  return (
    <div>
      <h6 className="mb-3 flex align-items-center gap-2">
        <i className="pi pi-check-circle text-green-600" style={{ marginRight: '5px' }}></i>
        <span>Stores Available for Complete Fulfillment</span>
        <Badge value={completeOptions.length} severity="success" style={{ marginLeft: '8px' }} />
      </h6>
      
      <div className="inventory-list-dark">
        <ul className="inventory-list-group-dark">
          {completeOptions.map((store) => (
            <li 
              key={store.storeCode} 
              className={`inventory-list-item-dark cursor-pointer ${
                selectedStoreCode === store.storeCode ? 'active' : ''
              }`}
              onClick={() => onStoreSelect(store.storeCode)}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="flex-1">
                  <div className="font-semibold">{store.storeName}</div>
                  <div className="text-sm text-color-secondary mt-1">
                    Total inventory: {store.totalAvailable} units available, {store.totalNeeded} needed
                  </div>
                </div>
                <div className="flex align-items-center gap-2">
                  <Badge 
                    value={`${store.totalAvailable} in stock`} 
                    severity="success" 
                  />
                  {selectedStoreCode === store.storeCode && (
                    <i className="pi pi-check-circle text-green-600"></i>
                  )}
                </div>
              </div>
              
              <div className="mt-3">
                <Accordion 
                  className="inventory-accordion"
                  activeIndex={selectedStoreCode === store.storeCode ? 0 : null}
                  onTabChange={(e) => {
                    if (e.index === 0) {
                      handleAccordionChange(store.storeCode);
                    }
                  }}
                >
                  <AccordionTab header={`View Item Details (${store.itemInventory.length} items)`}>
                    <div className="pt-2">
                      <DataTable 
                        value={prepareTableData(store)} 
                        size="small"
                        stripedRows
                        className="p-datatable-sm"
                        showGridlines
                      >
                        <Column 
                          field="storeName" 
                          header="Store" 
                          body={storeNameTemplate}
                          style={{ width: '25%' }}
                        />
                        <Column 
                          field="productName" 
                          header="Product" 
                          body={productNameTemplate}
                          style={{ width: '45%' }}
                        />
                        <Column 
                          field="quantityNeeded" 
                          header="Needed" 
                          body={neededTemplate}
                          style={{ width: '15%' }}
                        />
                        <Column 
                          field="quantityAvailable" 
                          header="Available" 
                          body={availableTemplate}
                          style={{ width: '15%' }}
                        />
                      </DataTable>
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {selectedStoreCode && (
        <div className="mt-4 p-3 border-1 surface-border border-round bg-green-50">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-info-circle text-green-600"></i>
            <span className="text-green-900">
              Ready to assign all {selectedLineItems.length} items to {
                completeOptions.find(store => store.storeCode === selectedStoreCode)?.storeName
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
}; 